import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; 
import { api } from '../../services/api'; 

interface Product {
  id: number;
  name: string;
  nameEn: string;
  salePrice: number;
}

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const product = (location.state as { product?: Product })?.product;
  const { isAuthenticated, token } = useAuth();

  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [cardRegion, setCardRegion] = useState('taiwan');
  const [invoiceType, setInvoiceType] = useState('donate');
  const [carrierType, setCarrierType] = useState('member'); 
  const [carrierValue, setCarrierValue] = useState(''); 
  
  const [savedCarriers, setSavedCarriers] = useState({
    phone: '',
    mobile: '',
    certificate: '',
    donateCode: '' 
  });

  const [companyEmail, setCompanyEmail] = useState('');
  const [companyTaxId, setCompanyTaxId] = useState('');
  const [donateUnit, setDonateUnit] = useState('25999');
  const [loveCode, setLoveCode] = useState('');

  const [errorMsg, setErrorMsg] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // 新增：控制重複購買確認 Modal 的狀態
  const [showRepurchaseConfirm, setShowRepurchaseConfirm] = useState(false);

  useEffect(() => {
    const fetchCarriers = async () => {
      if (isAuthenticated && token) {
        try {
          const data = await api.getInvoiceCarriers(token);
          if (data) {
            setSavedCarriers({
              phone: data.carrier_phone || '',
              mobile: data.carrier_mobile || '',
              certificate: data.carrier_cert || '',
              donateCode: data.donate_code || ''
            });
            setCarrierValue(data.carrier_phone || '');
            setCompanyEmail(data.company_email || '');
            setCompanyTaxId(data.company_tax_id || '');
          }
        } catch (error) {
          console.error('無法讀取記憶載具', error);
        }
      }
    };
    fetchCarriers();
  }, [isAuthenticated, token]);

  const handleCarrierTypeChange = (newType: string) => {
    setCarrierType(newType);
    setErrorMsg(''); 
    if (newType === 'member') setCarrierValue(savedCarriers.phone);
    else if (newType === 'mobile') setCarrierValue(savedCarriers.mobile);
    else if (newType === 'certificate') setCarrierValue(savedCarriers.certificate);
  };

  // 第一階段：表單驗證與重複購買檢查
  const handleCheckout = async () => {
    setErrorMsg('');

    if (!product) {
      setErrorMsg('找不到商品資訊，請重新選購');
      return;
    }

    if (paymentMethod === 'credit_card' && cardRegion === 'taiwan') {
      if (invoiceType === 'personal') {
        const val = carrierValue.trim();
        if (!val) { setErrorMsg('載具號碼不可為空白'); return; }
        if (carrierType === 'member' && !/^09\d{8}$/.test(val)) { setErrorMsg('請輸入有效的手機號碼 (例如: 0912345678)'); return; }
        if (carrierType === 'mobile' && !/^\/[0-9A-Z.+\-]{7}$/.test(val)) { setErrorMsg('請輸入有效的手機條碼 (例如: /AB12345，注意需大寫)'); return; }
        if (carrierType === 'certificate' && !/^[A-Z]{2}\d{14}$/.test(val)) { setErrorMsg('請輸入有效的自然人憑證 (例如: TW12345678901234，注意需大寫)'); return; }
      }
      else if (invoiceType === 'company') {
        const emailVal = companyEmail.trim();
        const taxIdVal = companyTaxId.trim();
        if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) { setErrorMsg('請輸入有效的電子信箱格式'); return; }
        if (!taxIdVal || !/^\d{8}$/.test(taxIdVal)) { setErrorMsg('請輸入有效的 8 碼統一編號'); return; }
      }
      else if (invoiceType === 'donate' && donateUnit === 'other') {
        const codeVal = loveCode.trim();
        if (!codeVal) { setErrorMsg('愛心碼不可為空白'); return; }
        if (!/^\d{3,7}$/.test(codeVal)) { setErrorMsg('請輸入有效的 3~7 碼數字愛心碼'); return; }
      }
    }

    setIsProcessing(true);

    if (isAuthenticated && token) {
      try {
        const myKeys = await api.getMyKeys(token);
        const hasPurchased = myKeys.some((key: any) => key.gameName === product.name);
        
        if (hasPurchased) {
          setIsProcessing(false);
          setShowRepurchaseConfirm(true); // 觸發自訂 Modal
          return; // 中斷流程，等待使用者決定
        }
      } catch (error) {
        console.error('檢查購買紀錄失敗', error);
      }
    }

    // 若無重複購買，直接執行結帳
    executeCheckout();
  };

  // 第二階段：實際執行 API 送出
  const executeCheckout = async () => {
    setIsProcessing(true);
    setShowRepurchaseConfirm(false); // 確保 Modal 關閉

    try {
      if (cardRegion === 'taiwan' && isAuthenticated && token) {
        if (invoiceType === 'personal') {
          await api.saveInvoiceCarrier(token, { type: carrierType, value: carrierValue.trim() });
        } else if (invoiceType === 'company') {
          await api.saveInvoiceCarrier(token, { type: 'company', email: companyEmail.trim(), taxId: companyTaxId.trim() });
        } else if (invoiceType === 'donate' && donateUnit === 'other') {
          await api.saveInvoiceCarrier(token, { type: 'donate', value: loveCode.trim() });
        }
      }

      const orderPayload: any = {
        productId: product?.id,
        amount: product?.salePrice,
        paymentMethod: paymentMethod, 
        cardRegion: paymentMethod === 'credit_card' ? cardRegion : null
      };

      if (paymentMethod === 'credit_card' && cardRegion === 'taiwan') {
        const invoiceData: any = { invoice: {} };

        if (invoiceType === 'donate') {
          invoiceData.invoice = {
            invoiceType: 1,
            donateNpo: donateUnit === 'other' ? loveCode.trim() : donateUnit
          };
        } 
        else if (invoiceType === 'personal') {
          invoiceData.invoice = {
            invoiceType: 2
          };
          
          if (carrierType === 'mobile') {
            invoiceData.invoice.carrierType = '3J0002';
            invoiceData.invoice.carrierId1 = carrierValue.trim();
            invoiceData.invoice.carrierId2 = carrierValue.trim();
          } else if (carrierType === 'certificate') {
            invoiceData.invoice.carrierType = 'CQ0001';
            invoiceData.invoice.carrierId1 = carrierValue.trim();
            invoiceData.invoice.carrierId2 = carrierValue.trim();
          } else if (carrierType === 'member') {
            invoiceData.invoice.carrierType = 'EG0379';
            invoiceData.invoice.invoicePhone = carrierValue.trim();
          }
        } 
        else if (invoiceType === 'company') {
          invoiceData.invoice = {
            invoiceType: 3,
            carrierType: 'EG0379',
            invoiceEmail: companyEmail.trim(),
            invoiceCompanyNumber: companyTaxId.trim()
          };
        }
        
        orderPayload.invoiceInfo = invoiceData;
      }

      if (token) {
        const response = await api.submitOrder(token, orderPayload);
        
        if (response.payUrl && response.params) {
          navigate('/checkout/post', { 
            state: { 
              payUrl: response.payUrl, 
              params: response.params 
            } 
          });
        } else {
          alert(response.message || '訂單建立完成');
          navigate('/member');
        }

      } else {
        throw new Error('登入憑證已失效，請重新登入');
      }

    } catch (error: any) {
      setErrorMsg(error.message || '結帳發生錯誤，請稍後再試');
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* 處理中遮罩 */}
      {isProcessing && !showRepurchaseConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl flex flex-col items-center shadow-2xl animate-fade-in-up">
            <i className="ri-loader-4-line text-5xl text-accent-orange animate-spin mb-4"></i>
            <h3 className="text-lg font-bold text-text-dark mb-1">訂單處理中</h3>
            <p className="text-sm text-text-muted">請勿關閉視窗或重新整理頁面...</p>
          </div>
        </div>
      )}

      {/* 自訂：重複購買防呆 Modal */}
      {showRepurchaseConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in-up">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-accent-orange/10 mb-4 mx-auto">
              <i className="ri-error-warning-fill text-2xl text-accent-orange"></i>
            </div>
            <h3 className="text-xl font-bold text-center text-text-dark mb-2">溫馨提醒</h3>
            <p className="text-center text-text-muted text-sm mb-6">
              該商品您已經購買過，確定要再次購買嗎？
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowRepurchaseConfirm(false)}
                autoFocus // 核心：預設將鍵盤與點擊焦點設定在「取消」上
                className="flex-1 px-4 py-2.5 rounded-lg border border-fog-dark text-text-dark font-medium hover:bg-fog-gray transition-colors focus:outline-none focus:ring-2 focus:ring-accent-orange focus:ring-offset-1"
              >
                取消
              </button>
              <button 
                onClick={executeCheckout}
                className="flex-1 px-4 py-2.5 rounded-lg bg-accent-rose text-white font-medium hover:bg-accent-rose/90 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-rose focus:ring-offset-1"
              >
                確定購買
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full px-6 md:px-10 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <p className="text-text-muted text-sm mb-1">準備結帳囉</p>
            <h1 className="text-text-dark text-2xl md:text-4xl font-heading font-bold tracking-wide">
              快帶它回家！
            </h1>
          </div>

          <div className="rounded-xl bg-white border border-fog-mid p-6 md:p-8 mb-8">
            <table className="w-full">
              <thead>
                <tr className="border-b border-fog-mid">
                  <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider pb-4 w-16">項目</th>
                  <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider pb-4">遊戲名稱</th>
                  <th className="text-right text-text-muted text-xs font-medium uppercase tracking-wider pb-4 w-24">價格</th>
                </tr>
              </thead>
              <tbody>
                {product ? (
                  <tr>
                    <td className="py-4 text-text-dark text-sm">1</td>
                    <td className="py-4">
                      <p className="text-text-dark text-sm font-medium">{product.name}</p>
                      <p className="text-text-muted text-xs">{product.nameEn}</p>
                    </td>
                    <td className="py-4 text-right">
                      <span className="text-accent-orange text-sm font-bold">NT${product.salePrice}</span>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="py-10 text-center">
                      <i className="ri-shopping-cart-line text-4xl text-text-gray mb-3 block"></i>
                      <p className="text-text-muted text-sm mb-4">購物車空空如也～去逛逛吧！</p>
                      <Link to="/" className="inline-flex items-center gap-1.5 text-accent-orange hover:underline text-sm cursor-pointer">
                        <i className="ri-arrow-left-line"></i> 回到首頁挖寶
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {product && (
              <div className="mt-6 pt-6 border-t border-fog-mid flex justify-between items-center">
                <span className="text-text-dark text-sm font-medium">總計</span>
                <span className="text-accent-orange text-xl font-bold">NT${product.salePrice}</span>
              </div>
            )}
          </div>

          {product && (
            <div>
              <h3 className="text-text-dark text-sm font-semibold mb-4 font-heading">支付方式</h3>
              <div className="flex flex-wrap gap-3">
                <div 
                  onClick={() => !isProcessing && setPaymentMethod('credit_card')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                    isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    paymentMethod === 'credit_card' ? 'bg-accent-orange/10 border-accent-orange text-accent-orange' : 'bg-white border-fog-mid text-text-muted hover:border-accent-orange/50'
                  }`}
                >
                  <i className="ri-bank-card-line text-lg"></i> 信用卡
                </div>
                
                <div 
                  onClick={() => !isProcessing && setPaymentMethod('test_payment')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                    isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    paymentMethod === 'test_payment' ? 'bg-accent-orange/10 border-accent-orange text-accent-orange' : 'bg-white border-fog-mid text-text-muted hover:border-accent-orange/50'
                  }`}
                >
                  <i className="ri-flask-line text-lg"></i> 測試金流
                </div>
              </div>

              {paymentMethod === 'credit_card' && (
                <div className="mt-6 p-5 sm:p-6 bg-fog-gray/20 rounded-lg border border-fog-mid space-y-6 relative">
                  
                  {isProcessing && <div className="absolute inset-0 z-10"></div>}

                  <div>
                    <h4 className="text-text-dark text-sm font-medium mb-3">發卡地區</h4>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <div className="relative flex items-center justify-center w-4 h-4">
                          <input type="radio" name="cardRegion" value="taiwan" checked={cardRegion === 'taiwan'} onChange={() => { setCardRegion('taiwan'); setErrorMsg(''); }} className="peer appearance-none w-4 h-4 border border-text-gray rounded-full checked:border-accent-orange cursor-pointer transition-colors" />
                          <div className="absolute w-2 h-2 rounded-full bg-accent-orange opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                        </div>
                        <span className="text-sm text-text-dark group-hover:text-accent-orange transition-colors">台灣</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer group">
                        <div className="relative flex items-center justify-center w-4 h-4">
                          <input type="radio" name="cardRegion" value="overseas" checked={cardRegion === 'overseas'} onChange={() => { setCardRegion('overseas'); setErrorMsg(''); }} className="peer appearance-none w-4 h-4 border border-text-gray rounded-full checked:border-accent-orange cursor-pointer transition-colors" />
                          <div className="absolute w-2 h-2 rounded-full bg-accent-orange opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                        </div>
                        <span className="text-sm text-text-dark group-hover:text-accent-orange transition-colors">國外</span>
                      </label>
                    </div>
                  </div>

                  {cardRegion === 'taiwan' && (
                    <div className="pt-6 border-t border-fog-mid">
                      <h4 className="text-text-dark text-sm font-medium mb-3">電子發票資訊</h4>
                      
                      <div className="p-4 bg-white border border-fog-dark rounded-md text-xs text-text-muted leading-relaxed mb-4">
                        若您選擇電子發票，系統將會優先以網站會員帳號作為儲存電子發票載具。您亦可另選擇手機條碼載具或自然人憑證條碼載具等共通性載具(需先至財政部電子發票平台申請)。於本網站消費開立之統一發票將不主動寄送。您可隨時於『發票查詢』發票圖檔消費後3個月內可索取紙本發票逾期將自動捐贈，詳情請參考「統一發票處理流程」。三聯式發票：若您儲值時有填寫統一編號，統一發票將於次月寄出。捐贈發票：若您訂購時選擇捐贈，將會在單月25號開獎後，由「財政部電子發票整合服務平台」對獎後匯入指定捐贈單位帳戶。
                      </div>

                      <div className="p-5 border border-dashed border-fog-dark bg-white rounded-md flex flex-col gap-6">
                        
                        <div className="flex flex-col md:flex-row gap-5">
                          <label className="flex items-center gap-2 cursor-pointer group flex-1">
                            <div className="relative flex items-center justify-center w-4 h-4">
                              <input type="radio" name="invoiceType" value="personal" checked={invoiceType === 'personal'} onChange={() => { setInvoiceType('personal'); handleCarrierTypeChange('member'); }} className="peer appearance-none w-4 h-4 border border-text-gray rounded-sm checked:border-accent-orange checked:bg-accent-orange cursor-pointer transition-colors" />
                              <i className="ri-check-line absolute text-white text-xs opacity-0 peer-checked:opacity-100 pointer-events-none"></i>
                            </div>
                            <span className="text-sm text-text-dark group-hover:text-accent-orange transition-colors">電子發票(個人)</span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer group flex-1">
                            <div className="relative flex items-center justify-center w-4 h-4">
                              <input type="radio" name="invoiceType" value="company" checked={invoiceType === 'company'} onChange={() => { setInvoiceType('company'); setErrorMsg(''); }} className="peer appearance-none w-4 h-4 border border-text-gray rounded-sm checked:border-accent-orange checked:bg-accent-orange cursor-pointer transition-colors" />
                              <i className="ri-check-line absolute text-white text-xs opacity-0 peer-checked:opacity-100 pointer-events-none"></i>
                            </div>
                            <span className="text-sm text-text-dark group-hover:text-accent-orange transition-colors">電子發票(統編)</span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer group flex-1">
                            <div className="relative flex items-center justify-center w-4 h-4">
                              <input type="radio" name="invoiceType" value="donate" checked={invoiceType === 'donate'} onChange={() => { setInvoiceType('donate'); setErrorMsg(''); }} className="peer appearance-none w-4 h-4 border border-text-gray rounded-sm checked:border-accent-orange checked:bg-accent-orange cursor-pointer transition-colors" />
                              <i className="ri-check-line absolute text-white text-xs opacity-0 peer-checked:opacity-100 pointer-events-none"></i>
                            </div>
                            <span className="text-sm text-text-dark group-hover:text-accent-orange transition-colors">發票捐贈(伊甸、家扶...等)</span>
                          </label>
                        </div>

                        {invoiceType === 'personal' && (
                          <div className="flex flex-col gap-4 pt-4 border-t border-fog-mid/50">
                            <select value={carrierType} onChange={(e) => handleCarrierTypeChange(e.target.value)} className="w-full sm:w-64 p-2.5 border border-fog-dark rounded-md text-sm text-text-dark bg-white focus:outline-none focus:border-accent-orange transition-colors cursor-pointer">
                              <option value="member">Nice Game 遊戲商城會員載具</option>
                              <option value="mobile">手機條碼載具</option>
                              <option value="certificate">自然人憑證載具</option>
                            </select>

                            {carrierType === 'member' && (
                              <div className="flex flex-col gap-2">
                                <div className="flex flex-col xl:flex-row xl:items-center gap-3">
                                  <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">手機號碼：</label>
                                  <input type="tel" value={carrierValue} onChange={(e) => { setCarrierValue(e.target.value); setErrorMsg(''); }} maxLength={10} className={`w-full xl:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors ${errorMsg && invoiceType === 'personal' && !carrierValue ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                                </div>
                                <p className="text-xs text-accent-orange">※用以中獎通知，請確實輸入。</p>
                              </div>
                            )}

                            {carrierType === 'mobile' && (
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">手機條碼：</label>
                                <input type="text" placeholder="例如：/AB12345" value={carrierValue} onChange={(e) => { setCarrierValue(e.target.value.toUpperCase()); setErrorMsg(''); }} maxLength={8} className={`w-full sm:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors uppercase ${errorMsg && invoiceType === 'personal' && !carrierValue ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                              </div>
                            )}

                            {carrierType === 'certificate' && (
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">憑證條碼：</label>
                                <input type="text" placeholder="例如：TW12345678901234" value={carrierValue} onChange={(e) => { setCarrierValue(e.target.value.toUpperCase()); setErrorMsg(''); }} maxLength={16} className={`w-full sm:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors uppercase ${errorMsg && invoiceType === 'personal' && !carrierValue ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                              </div>
                            )}
                          </div>
                        )}

                        {invoiceType === 'company' && (
                          <div className="flex flex-col gap-4 pt-4 border-t border-fog-mid/50">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">電子信箱：</label>
                              <input type="email" value={companyEmail} onChange={(e) => { setCompanyEmail(e.target.value); setErrorMsg(''); }} className={`w-full sm:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors ${errorMsg && invoiceType === 'company' && !companyEmail ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">統一編號：</label>
                              <input type="text" maxLength={8} value={companyTaxId} onChange={(e) => { setCompanyTaxId(e.target.value); setErrorMsg(''); }} className={`w-full sm:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors ${errorMsg && invoiceType === 'company' && !companyTaxId ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                            </div>
                            <p className="text-xs text-accent-orange">※請輸入統一編號，發票將會寄到您的信箱。</p>
                          </div>
                        )}

                        {invoiceType === 'donate' && (
                          <div className="flex flex-col gap-4 pt-4 border-t border-fog-mid/50">
                            <select
                              value={donateUnit}
                              onChange={(e) => {
                                const unit = e.target.value;
                                setDonateUnit(unit);
                                if (unit === 'other') setLoveCode(savedCarriers.donateCode); 
                                else setLoveCode('');
                                setErrorMsg('');
                              }}
                              className="w-full sm:w-64 p-2.5 border border-fog-dark rounded-md text-sm text-text-dark bg-white focus:outline-none focus:border-accent-orange transition-colors cursor-pointer"
                            >
                              <option value="25999">25999 忠義基金會</option>
                              <option value="885521">885521 兒福聯盟</option>
                              <option value="other">其他捐贈單位</option>
                            </select>

                            {donateUnit === 'other' && (
                              <div className="flex flex-col gap-2 mt-1">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                  <label className="text-sm text-text-dark font-medium whitespace-nowrap min-w-[70px]">請輸入愛心碼：</label>
                                  <input type="text" value={loveCode} onChange={(e) => { setLoveCode(e.target.value); setErrorMsg(''); }} maxLength={7} className={`w-full sm:w-64 p-2 border rounded-md text-sm focus:outline-none transition-colors ${errorMsg && invoiceType === 'donate' && !loveCode ? 'border-accent-rose' : 'border-fog-dark focus:border-accent-orange'}`} />
                                </div>
                                <p className="text-xs text-accent-orange mt-1">※ 請輸入3~7碼數字(若輸入錯誤捐贈碼，則由本公司代為捐贈給其他社福團體)</p>
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    </div>
                  )}
                </div>
              )}

              {errorMsg && (
                <div className="mt-6 px-4 py-3 bg-accent-rose/10 border border-accent-rose/20 rounded-lg text-accent-rose text-sm font-medium flex items-center gap-2 relative z-10">
                  <i className="ri-error-warning-line text-lg"></i>
                  {errorMsg}
                </div>
              )}

              <button 
                onClick={handleCheckout}
                disabled={isProcessing}
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-accent-orange hover:bg-accent-orange/90 text-white text-sm font-semibold px-12 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:hover:scale-100 relative z-10"
              >
                {isProcessing ? (
                  <i className="ri-loader-4-line text-lg animate-spin"></i>
                ) : (
                  <i className="ri-check-line text-lg"></i>
                )}
                {isProcessing ? '處理中...' : '確認付款'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}